import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import WellnessProfileInformation from './components/WellnessProfileInformation';
import WellnessPreferences from './components/WellnessPreferences';
import BookingHistory from './components/BookingHistory';
import FavoriteCenters from './components/FavoriteCenters';
import WellnessJourney from './components/WellnessJourney';
import PrivacyControls from './components/PrivacyControls';
import SubscriptionManagement from './components/SubscriptionManagement';
import MobileTabNavigation from './components/MobileTabNavigation';
import DesktopTabNavigation from './components/DesktopTabNavigation';

const WellnessUserProfile = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved'); // 'saving', 'saved', 'error'

  // Mock user data - in real app this would come from context/state management
  const [user] = useState({
    id: '123',
    role: 'guest', // 'guest', 'host', 'practitioner'
    name: 'Sarah Chen',
    email: 'sarah.chen@example.com',
    phone: '+1 (555) 987-6543',
    avatar: '/assets/images/wellness_profile_default.png',
    wellnessGoals: ['stress-reduction', 'energy-enhancement', 'spiritual-growth'],
    energyLevel: 7.5,
    meditationStreak: 15,
    isVerified: true,
    joinedDate: '2024-01-15'
  });

  const tabs = [
    {
      id: 'profile',
      label: 'Wellness Profile',
      icon: 'User',
      component: WellnessProfileInformation,
      roles: ['all']
    },
    {
      id: 'preferences',
      label: 'Wellness Preferences',
      icon: 'Settings',
      component: WellnessPreferences,
      roles: ['all']
    },
    {
      id: 'journey',
      label: 'Wellness Journey',
      icon: 'TrendingUp',
      component: WellnessJourney,
      roles: ['guest']
    },
    {
      id: 'bookings',
      label: 'Booking History',
      icon: 'Calendar',
      component: BookingHistory,
      roles: ['guest']
    },
    {
      id: 'favorites',
      label: 'Favorite Centers',
      icon: 'Heart',
      component: FavoriteCenters,
      roles: ['guest']
    },
    {
      id: 'privacy',
      label: 'Privacy & Health Data',
      icon: 'Shield',
      component: PrivacyControls,
      roles: ['all']
    },
    {
      id: 'subscription',
      label: 'Subscriptions',
      icon: 'CreditCard',
      component: SubscriptionManagement,
      roles: ['guest']
    }
  ];

  const filteredTabs = tabs.filter(tab => 
    tab.roles.includes('all') || tab.roles.includes(user?.role)
  );

  useEffect(() => {
    // Simulate initial data loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Auto-save functionality for sensitive wellness data
    if (hasUnsavedChanges) {
      setAutoSaveStatus('saving');
      const saveTimer = setTimeout(() => {
        // Simulate API call with encryption for health data
        setAutoSaveStatus('saved');
        setHasUnsavedChanges(false);
      }, 2000);

      return () => clearTimeout(saveTimer);
    }
  }, [hasUnsavedChanges]);

  const handleTabChange = (tabId) => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved wellness data changes. Are you sure you want to leave this section?')) {
        setActiveTab(tabId);
        setHasUnsavedChanges(false);
        setIsMobileMenuOpen(false);
      }
    } else {
      setActiveTab(tabId);
      setIsMobileMenuOpen(false);
    }
  };

  const handleDataChange = () => {
    setHasUnsavedChanges(true);
  };

  const handleExportWellnessData = () => {
    // Export wellness journey data
    const exportData = {
      profile: user,
      wellnessJourney: {
        energyLevels: [], // Would contain actual tracking data
        meditationProgress: [],
        sessionHistory: [],
        transformationGoals: []
      },
      exportDate: new Date().toISOString(),
      dataType: 'wellness_data'
    };
    
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `wellness-data-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const ActiveTabComponent = filteredTabs.find(tab => tab.id === activeTab)?.component;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <Header />
        <div className="pt-16 lg:pt-18">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Header Skeleton */}
            <div className="mb-8">
              <div className="h-8 bg-blue-100 rounded w-1/3 mb-4 skeleton"></div>
              <div className="h-4 bg-blue-100 rounded w-1/2 skeleton"></div>
            </div>
            
            {/* Content Skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Sidebar Skeleton */}
              <div className="lg:col-span-1">
                <div className="space-y-2">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="h-12 bg-blue-100 rounded skeleton"></div>
                  ))}
                </div>
              </div>
              
              {/* Main Content Skeleton */}
              <div className="lg:col-span-3">
                <div className="space-y-6">
                  <div className="h-64 bg-blue-100 rounded-lg skeleton"></div>
                  <div className="h-48 bg-blue-100 rounded-lg skeleton"></div>
                  <div className="h-32 bg-blue-100 rounded-lg skeleton"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <Header />
      
      <main className="pt-16 lg:pt-18">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-slate-800 font-heading mb-2">
                  Wellness Profile & Journey
                </h1>
                <p className="text-slate-600">
                  Manage your wellness journey, EES preferences, and transformation goals
                </p>
              </div>
              
              {/* Auto-save Status & Export */}
              <div className="mt-4 md:mt-0 flex items-center space-x-4">
                {/* Wellness Progress Indicator */}
                <div className="hidden md:flex items-center space-x-3 bg-white/70 backdrop-blur-sm rounded-lg px-4 py-2 border border-blue-200">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    <span className="text-sm text-slate-600">Energy: {user.energyLevel}/10</span>
                  </div>
                  <div className="w-px h-4 bg-blue-200"></div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-sm text-slate-600">{user.meditationStreak} day streak</span>
                  </div>
                </div>

                {/* Auto-save Status */}
                <div className="flex items-center space-x-2 text-sm">
                  {autoSaveStatus === 'saving' && (
                    <>
                      <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-slate-600">Saving wellness data...</span>
                    </>
                  )}
                  {autoSaveStatus === 'saved' && (
                    <>
                      <div className="w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center">
                        <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-emerald-600">Secured</span>
                    </>
                  )}
                  {autoSaveStatus === 'error' && (
                    <>
                      <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                      <span className="text-red-600">Save failed</span>
                    </>
                  )}
                </div>
                
                {/* Export Button */}
                <button
                  onClick={handleExportWellnessData}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors duration-200"
                >
                  Export Wellness Data
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Tab Navigation */}
          <div className="lg:hidden mb-6">
            <MobileTabNavigation
              tabs={filteredTabs}
              activeTab={activeTab}
              onTabChange={handleTabChange}
              isMenuOpen={isMobileMenuOpen}
              onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            />
          </div>

          {/* Desktop Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Desktop Sidebar Navigation */}
            <div className="hidden lg:block lg:col-span-1">
              <div className="lg:sticky lg:top-20 lg:max-h-screen lg:overflow-y-auto lg:pb-20">
                <DesktopTabNavigation
                  tabs={filteredTabs}
                  activeTab={activeTab}
                  onTabChange={handleTabChange}
                />
              </div>
            </div>

            {/* Main Content Area */}
            <div className="lg:col-span-3">
              <div className="min-h-screen lg:min-h-0">
                {ActiveTabComponent && (
                  <ActiveTabComponent
                    user={user}
                    onDataChange={handleDataChange}
                    hasUnsavedChanges={hasUnsavedChanges}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default WellnessUserProfile;